import React, {useCallback, useState} from 'react'
import {Form, FormDataContext} from '@modifi/plugin-forms'
import {Caption, Col, DangerAlert, H5, Row} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {COMPANY_CIN_CODE, COMPANY_FOUNDATION_DATE} from '@modifi/plugin-onboarding'

import {COMPANY_FOUNDATION_MONTH, COMPANY_FOUNDATION_YEAR, COMPANY_NAME_SELECT} from '../constants'
import {EntityFormData} from '../../../../types/common'
import CompanyIntroductionFormContent from './company-introduction-form-content'
import CompanyFormFooter from '../company-form-footer'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchCompany} from '../../../../data/company'
import {aFetchCompanyRegistrationByExternalId} from '../../../../data/company-registration'

export type CompanyIntroductionProps = {
  possibleCompanyMatches?: {
    address?: {
      companyName?: string
      streetName?: string
      streetNumber?: string
      state?: string
      zipCode?: string
      city?: string
      countryCode?: string
    }
    providerCompanyId?: string
  }[]
  onSubmit: (changes: Record<string, unknown>) => void
  onDiscard: () => void
  saveErrorMessage?: string
  savePending: boolean
  formData: EntityFormData
  redirectToCompanyFinancials: () => void
}

const CompanyIntroduction: React.FC<CompanyIntroductionProps> = ({
  possibleCompanyMatches = [],
  onSubmit,
  onDiscard,
  saveErrorMessage = '',
  savePending,
  formData,
  redirectToCompanyFinancials,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [consent, setConsent] = useState(true)

  const toggleConsentChange = () => setConsent((prevConsent: any) => !prevConsent)

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      const dataOnSelection = changes

      if (!changes?.[COMPANY_NAME_SELECT]) {
        dataOnSelection[COMPANY_NAME_SELECT] = possibleCompanyMatches?.[0]?.providerCompanyId
      }

      if (changes[COMPANY_FOUNDATION_MONTH] || changes[COMPANY_FOUNDATION_YEAR]) {
        let month = changes[COMPANY_FOUNDATION_MONTH]
        let year = changes[COMPANY_FOUNDATION_YEAR]
        if (!month) {
          month = formData.initialValues?.[COMPANY_FOUNDATION_MONTH]
        }
        if (!year) {
          year = formData.initialValues?.[COMPANY_FOUNDATION_YEAR]
        }
        if (month + 1 < 10) {
          month = `0${month + 1}`
        } else {
          month = `${month + 1}`
        }

        dataOnSelection[COMPANY_FOUNDATION_DATE] = `${year}-${month}-01`

        delete dataOnSelection[COMPANY_FOUNDATION_MONTH]
        delete dataOnSelection[COMPANY_FOUNDATION_YEAR]
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'intro_step_1_submit',
        element_id: 'intro_step_1_submit',
        element_type: 'button',
      })

      onSubmit(dataOnSelection)
    },
    [formData.initialValues, onSubmit, possibleCompanyMatches]
  )

  const handleForwardButtonClick = useCallback(() => {
    if (!saveErrorMessage) {
      onDiscard()
      redirectToCompanyFinancials()
    }
  }, [onDiscard, redirectToCompanyFinancials, saveErrorMessage])

  return (
    <>
      <Row>
        <Col>
          {/* @todo_pre_underwriting using t function for translation is giving error in console:
            Cannot update a component (`ProgressSteps`) while rendering a different component
            (`CompanyIntroduction`). To locate the bad setState() call inside `CompanyIntroduction` */}
          <H5>{t('intro-page.company-introduction.headline')}</H5>
        </Col>
      </Row>
      <Row mb={24}>
        <Col>
          <Caption>{t('intro-page.company-introduction.description')}</Caption>
        </Col>
      </Row>
      {saveErrorMessage && (
        <Row>
          <Col>
            <DangerAlert>{saveErrorMessage}</DangerAlert>
          </Col>
        </Row>
      )}
      <Form
        isLoading={savePending}
        defaultValues={formData?.defaultValues}
        initialValues={formData?.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <CompanyIntroductionFormContent
          possibleCompanyMatches={possibleCompanyMatches}
          handleConsentChange={toggleConsentChange}
          consent={consent}
        />
        <CompanyUnlockRefresher
          refetch={(): any[] => [aFetchCompany(), aFetchCompanyRegistrationByExternalId()]}
          initialValues={formData?.initialValues}
          defaultValues={formData?.defaultValues}
        />
        <FormDataContext.Consumer>
          {({data}) => (
            <CompanyFormFooter
              hasError={!!saveErrorMessage}
              loading={savePending}
              handleForwardButtonClick={handleForwardButtonClick}
              disableSubmit={!!(data?.[COMPANY_CIN_CODE] && !consent)}
              dataTargetSuffix="from-step-1"
            />
          )}
        </FormDataContext.Consumer>
      </Form>
    </>
  )
}

export default CompanyIntroduction
